import { makeAutoObservable } from 'mobx'
import { RootStore } from 'stores'
import {
  searchTrainingAssignments,
  createTrainingAssignment,
  updateTrainingAssignmentById,
  archiveMultipleTrainingAssignments,
  restoreMultipleTrainingAssignments,
  getTrainingAssignmentDetail,
  getActivityLogs,
  IActivityLogQueryParams
} from 'API/training-assignment'
import {
  ITrainingAssignment,
  ITrainingAssignmentSearchFilterDto,
  ICreateTrainingAssignmentDto,
  ITrainingAssignmentDetail,
  IActivityLogItem,
  ActivityType
} from 'interfaces/training-assignment'

class TrainingAssignmentStore {
  rootStore: RootStore
  assignments: ITrainingAssignment[] = []
  numberOfAssignments: number = 0
  isFetchingList: boolean = false
  isManageMode: boolean = false
  assignmentDetail: ITrainingAssignmentDetail | null = null
  selectedEnrollmentId: number | null = null
  isFetchingDetail: boolean = false

  // Activity logs state
  activityLogs: IActivityLogItem[] = []
  activityLogNextCursor: string | null = null
  activityLogNextCursorId: number | null = null
  isFetchingActivityLogs: boolean = false
  hasMoreActivityLogs: boolean = false

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    makeAutoObservable(this)
  }

  setManageMode(status: boolean): void {
    this.isManageMode = status
  }

  async searchAssignments(filterDto: ITrainingAssignmentSearchFilterDto, orgId?: number): Promise<void> {
    this.isFetchingList = true
    try {
      const { results, totalCount } = await searchTrainingAssignments(filterDto, orgId)
      this.assignments = results
      this.numberOfAssignments = totalCount
    } finally {
      this.isFetchingList = false
    }
  }

  async createAssignment(dto: ICreateTrainingAssignmentDto, orgId?: number): Promise<ITrainingAssignment> {
    return createTrainingAssignment(dto, orgId)
  }

  async updateAssignmentStatus(id: number, status: number): Promise<void> {
    await updateTrainingAssignmentById(id, { status } as any)
  }

  async archiveMultiple(ids: number[]): Promise<void> {
    await archiveMultipleTrainingAssignments(ids)
  }

  async restoreMultiple(ids: number[]): Promise<void> {
    await restoreMultipleTrainingAssignments(ids)
  }

  async getAssignmentDetail(id: number, enrollmentId?: number): Promise<void> {
    this.isFetchingDetail = true
    try {
      const detail = await getTrainingAssignmentDetail(id, enrollmentId)
      this.assignmentDetail = detail
      if (detail.selectedEnrollment) {
        this.selectedEnrollmentId = detail.selectedEnrollment._id
      }
    } finally {
      this.isFetchingDetail = false
    }
  }

  setSelectedEnrollmentId(id: number | null): void {
    this.selectedEnrollmentId = id
  }

  clearDetail(): void {
    this.assignmentDetail = null
    this.selectedEnrollmentId = null
    this.isFetchingDetail = false
  }

  // Activity logs — initial fetch (resets state)
  async fetchActivityLogs(
    assignmentId: number,
    type?: ActivityType,
    membershipId?: number,
    sort: 'newest' | 'oldest' = 'newest',
    limit: number = 20
  ): Promise<void> {
    this.isFetchingActivityLogs = true
    this.activityLogs = []
    this.activityLogNextCursor = null
    this.activityLogNextCursorId = null
    this.hasMoreActivityLogs = false

    try {
      const params: IActivityLogQueryParams = { assignmentId, limit }
      if (type) params.type = type
      if (membershipId) params.membershipId = membershipId
      params.sort = sort

      const { items, nextCursor, nextCursorId } = await getActivityLogs(params)
      this.activityLogs = items
      this.activityLogNextCursor = nextCursor
      this.activityLogNextCursorId = nextCursorId
      this.hasMoreActivityLogs = nextCursor !== null
    } catch (error) {
      console.error('Failed to fetch activity logs:', error)
      this.activityLogs = []
    } finally {
      this.isFetchingActivityLogs = false
    }
  }

  // Activity logs — load more (cursor pagination)
  async loadMoreActivityLogs(
    assignmentId: number,
    type?: ActivityType,
    membershipId?: number,
    sort: 'newest' | 'oldest' = 'newest',
    limit: number = 20
  ): Promise<void> {
    if (!this.activityLogNextCursor || this.isFetchingActivityLogs) return

    this.isFetchingActivityLogs = true
    try {
      const params: IActivityLogQueryParams = {
        assignmentId,
        cursor: this.activityLogNextCursor,
        cursorId: this.activityLogNextCursorId ?? undefined,
        limit,
        sort
      }
      if (type) params.type = type
      if (membershipId) params.membershipId = membershipId

      const { items, nextCursor, nextCursorId } = await getActivityLogs(params)
      this.activityLogs = [...this.activityLogs, ...items]
      this.activityLogNextCursor = nextCursor
      this.activityLogNextCursorId = nextCursorId
      this.hasMoreActivityLogs = nextCursor !== null
    } catch (error) {
      console.error('Failed to load more activity logs:', error)
    } finally {
      this.isFetchingActivityLogs = false
    }
  }

  resetActivityLogs(): void {
    this.activityLogs = []
    this.activityLogNextCursor = null
    this.activityLogNextCursorId = null
    this.isFetchingActivityLogs = false
    this.hasMoreActivityLogs = false
  }
}

export default TrainingAssignmentStore
